import { computed, type ComputedRef, type Ref } from 'vue';
import { useFullArticleDetails } from '../api/articleDetails.api';
import {
  checkActionSupported,
  resolveArticleAction,
  PLEASED_ARTICLE_ACTIONS,
} from '@/common/bot/flow-frameworks/ChatMessageActions.static';
import type { ArticleAction } from '@/common/bot/types';
import { Bridge } from '@/common/bridge';
import { useArticleMetadata, type ArticleCheckState } from './useArticleMetadata';

interface UseArticleActionOptions {
  articleId: ComputedRef<string | number | undefined>;
}

interface UseArticleActionWithDepsOptions {
  articleConfig: ComputedRef<any>;
  articleCheck: Ref<ArticleCheckState>;
  canConnectToSupport: ComputedRef<boolean>;
  isLoggedIn: ComputedRef<boolean>;
}

// ============================================================================
// Internal Helper Functions
// ============================================================================

const checkIsKycAction = (articleConfig: ComputedRef<any>) => {
  return computed(() => {
    return articleConfig.value?.action === PLEASED_ARTICLE_ACTIONS.GO_KYC_VERIFICATION;
  });
};

const checkIsRegisterAction = (articleConfig: ComputedRef<any>) => {
  return computed(() => {
    return articleConfig.value?.action === PLEASED_ARTICLE_ACTIONS.GO_REGISTER;
  });
};

const resolveArticleActionFromConfig = (articleConfig: ComputedRef<any>, articleCheck: Ref<ArticleCheckState>) => {
  return computed<ArticleAction | null>(() => {
    if (!articleConfig.value) return null;

    const hasAction = Boolean(articleConfig.value.action);
    if (!hasAction) return null;

    const isSupported = checkActionSupported({
      action: articleConfig.value.action,
    });

    if (!isSupported) return null;

    const action = resolveArticleAction({
      action: articleConfig.value.action,
      actionMessage: articleConfig.value.actionMessage,
      actionTitle: articleConfig.value.actionText,
    } as ArticleAction);

    if (articleCheck.value.applied && articleCheck.value.action) {
      return articleCheck.value.action;
    }

    return action;
  });
};

const determineActionVisibility = (options: {
  articleAction: ComputedRef<ArticleAction | null>;
  isKycAction: ComputedRef<boolean>;
  isRegisterAction: ComputedRef<boolean>;
  isLoggedIn: ComputedRef<boolean>;
  canConnectToSupport: ComputedRef<boolean>;
  articleCheck: Ref<ArticleCheckState>;
}) => {
  const { articleAction, isKycAction, isRegisterAction, isLoggedIn, canConnectToSupport, articleCheck } = options;

  return computed(() => {
    const showWarning = !!articleCheck.value?.showWarning;
    const warningMessage = articleCheck.value?.warningMessage || '';

    if (!articleAction.value) {
      return { box: false, button: false, warning: false, warningMessage: '' };
    }

    const showBox = isKycAction.value ? isLoggedIn.value : isRegisterAction.value ? !isLoggedIn.value : true;
    const showButton = isKycAction.value ? showWarning : true;

    if (canConnectToSupport.value) {
      return { box: showBox, button: showButton, warning: showWarning, warningMessage };
    }
    return { box: showBox, button: false, warning: showWarning, warningMessage };
  });
};

const createNavigationHandler = (articleAction: ComputedRef<ArticleAction | null>) => {
  const goToArticleAction = () => {
    if (!articleAction.value) return;
    Bridge.goToAction(articleAction.value.action);
  };

  return goToArticleAction;
};

// ============================================================================
// Main Composables
// ============================================================================

const useArticleActionWithDeps = (options: UseArticleActionWithDepsOptions) => {
  const { articleConfig, articleCheck, canConnectToSupport, isLoggedIn } = options;

  const isKycAction = checkIsKycAction(articleConfig);
  const isRegisterAction = checkIsRegisterAction(articleConfig);
  const articleAction = resolveArticleActionFromConfig(articleConfig, articleCheck);
  const canShowAction = determineActionVisibility({
    articleAction,
    isKycAction,
    isRegisterAction,
    isLoggedIn,
    canConnectToSupport,
    articleCheck,
  });
  const goToArticleAction = createNavigationHandler(articleAction);

  return {
    canShowAction,
    articleAction,
    goToArticleAction,
  };
};

/**
 * Composable for handling article actions
 * Fetches article details and determines available actions
 *
 * @example
 * ```ts
 * const articleId = computed(() => router.currentRoute.params?.id);
 *
 * const {
 *   articleAction,
 *   isKycAction,
 *   goToArticleAction
 * } = useArticleAction({ articleId });
 * ```
 */
export const useArticleAction = (options: UseArticleActionOptions) => {
  const { articleId } = options;

  const { data: articleData } = useFullArticleDetails(articleId);
  const { articleCheck, canConnectToSupport, isLoggedIn } = useArticleMetadata({ articleId });

  const articleConfig = computed(() => articleData.value?.articleConfig || null);

  return useArticleActionWithDeps({
    articleConfig,
    articleCheck,
    canConnectToSupport,
    isLoggedIn,
  });
};
